import gettext from '@sdk/lang/gettext';
import {
  IntegrationStatus,
  PosIntegrations,
  IntegrationPlatform,

} from '@sdk/integrations/types';

import config from '../config';

import {
  INTEGRATION_STATUS,
  INTEGRATION_MODAL,
  INTEGRATIONS_DETAILS,
  INTEGRATIONS_INFO,
} from './constants';
import statusHtml from './components/integrationStatus.html';
import toastModalUrl from './modals/modal-toast-connected.html';
import locationMappingModalUrl from './modals/modal-location-mapping.html';
import LocationMappingCtrl from './modals/LocationMappingCtrl';
import userMappingModalUrl from './modals/modal-user-mapping.html';
import UserMappingCtrl from './modals/UserMappingCtrl';

export default class IntegrationService {
  modal;
  _integrations; // for available integrations caching
  _getIntegrationsPromise; // to avoid race conditions
  _mappingModalShown; // to avoid opening multiple modals at the same time

  constructor(
    $state,
    $rootScope,
    $uibModal,
    Authentication,
    IntegrationParametersModal,
    ConnectCodeModal,
    AlertService,
    SlingAnalytics
  ) {
    'ngInject';

    Object.assign(this, {
      $state,
      $rootScope,
      $uibModal,
      Authentication,
      IntegrationParametersModal,
      ConnectCodeModal,
      AlertService,
      SlingAnalytics,
    });

    this.mappingHandlers = {
      [INTEGRATION_STATUS.LOCATION_MAPPING]: this.showLocationMappingModal,
      [INTEGRATION_STATUS.USER_MAPPING]: this.showUserMappingModal,
    };
  }

  init() {
    this.setupNotifications({
      presentModalFor: [
        INTEGRATION_STATUS.INITIALIZING,
        INTEGRATION_STATUS.SYNCING,
        INTEGRATION_STATUS.ENABLED,
        INTEGRATION_STATUS.ERROR,
        INTEGRATION_STATUS.ERROR_OAUTH,
        INTEGRATION_STATUS.ERROR_BILLING,
        INTEGRATION_STATUS.ERROR_CONFIG,
        INTEGRATION_STATUS.LOCATION_MAPPING,
        INTEGRATION_STATUS.USER_MAPPING,
        INTEGRATION_STATUS.SHIFT_IMPORT,
      ],
    });
  }

  async getIntegrationByPlatform(platform) {
    const integrations = await this.getAvailableIntegrations();
    if (!integrations) {
      return false;
    }
    return integrations.find((integration) => integration.type === platform);
  }

  async getAvailableIntegrations(refresh = false) {
    if (!this.Authentication.user.isAdmin && !this.Authentication.user.isManager) {
      return;
    }

    if (!this._getIntegrationsPromise || refresh) {
      try {
        this._getIntegrationsPromise = this.Authentication.sendRequest({
          method: 'get',
          path: 'integrations',
        });

        this._integrations = (await this._getIntegrationsPromise).data.map((integration) => ({
          ...integration,
          icon: `/assets/integrations/${integration.name
            .toLowerCase()
            .replace(/ /g, '_')}-icon.svg`,
          healthy: integration.enabled && integration.status === INTEGRATION_STATUS.ENABLED,
        }));
      } catch (e) {
        this._integrations = [];
        this.AlertService.danger(gettext('Failed to retrieve integrations'));
      }

      this.$rootScope.$emit('integrations:updated', this._integrations);

      this.checkIntegrationMappingErrors(this._integrations);
    } else {
      await this._getIntegrationsPromise;
    }

    return this._integrations;
  }

  getIntegrationDetails(platform) {
    return INTEGRATIONS_DETAILS[platform];
  }

  async connect(integration) {
    // we take the integration from available ones in case it would fetch new data from the server due to invalidated cache
    const integrations = await this.getAvailableIntegrations();
    const integrationType = typeof integration === 'string' ? integration : integration.type;
    const integrationObj = integrations.find(({ type }) => type === integrationType) || integration;

    // In case of Square we would redirect to integrations side install url; this should work for most platforms
    // which should be gradually moved to the install url, just Shopify and other parametrized platforms would
    // require some additional parameter handling
    if (integrationType === 'square') {
      const url = `${config.SLING_INTEGRATIONS_URL}/install/${integrationObj.type}`;
      location.href = url;
      return;
    }

    location.href = integrationObj.url;
  }

  disconnect = (integration) => {
    this._integrations = null;
    delete this._getIntegrationsPromise;

    return this.Authentication.sendRequest({
      method: 'delete',
      path: `integrations/${integration.type}`,
    });
  };

  parseParameters(integration, inputParams) {
    return integration.parameters.reduce((acc, param) => {
      let val = inputParams[param.name];

      if (param.mapping) {
        const [name, regex] = param.mapping;
        const matches = new RegExp(regex).exec(inputParams[name]);
        if (matches) {
          val = matches[1];
        }
      }

      if (!val) {
        return acc;
      }

      return { ...acc, [param.name]: val };
    }, {});
  }

  async setParameters(integration, payload) {
    this._integrations = null;
    delete this._getIntegrationsPromise;

    return this.Authentication.sendRequest({
      method: 'post',
      path: `integrations/${integration.type}`,
      payload,
    });
  }

  oauth(integration, code, origin = null, state = null) {
    return this.Authentication.sendRequest({
      method: 'post',
      path: `integrations/${integration.type}/oauth`,
      payload: { code, origin, state },
      httpConfig: { withCredentials: true },
    });
  }

  continueOauth(integration, company, paySchedule) {
    return this.Authentication.sendRequest({
      method: 'put',
      path: `integrations/${integration.type}/oauth`,
      payload: { company, paySchedule },
    });
  }

  sync = (integration, payload = {}) =>
    this.Authentication.sendRequest({
      method: 'post',
      path: `integrations/${integration.type}/sync`,
      payload,
    });

  async setupNotifications({ presentModalFor }) {
    // eslint-disable-next-line complexity
    const handler = async (
      event,
      { platform = null, status, enabled, initial_import: firstSync, actor_id: actorId } = {}
    ) => {
      /**
       * This function would call various modals in the result of incoming WebSocket messages.
       *
       * 1. if there's LOCATION_MAPPING status, that means Location Mapping tool should be presented,
       * 2. if there's USER_MAPPING status, that means User Mapping tool should be presented,
       * 3. if this is the first sync with Toast, we present information about this particular integration,
       * 4. in other case we would be reporting status change, but only if there's actorId coming with the
       *    webhook message - that would mean sync process was explicitly triggered by the user and not
       *    by the background scheduler.
       */
      if (!platform) {
        return;
      }

      this.closeStatusModal();

      const integration = await this.getIntegrationByPlatform(platform);
      integration.status = status;

      if (await this.checkIntegrationMappingError(integration)) {
        return;
      }

      if (platform === PosIntegrations.toast && firstSync) {
        if (status === IntegrationStatus.enabled) {
          this.showToastIntegrationCompleteModal();
          return;
        }
      }

      if (actorId !== this.Authentication.user.id && !firstSync) {
        return;
      }

      if (presentModalFor && !presentModalFor.includes(INTEGRATION_STATUS[status])) {
        return;
      }

      if (enabled) {
        this.ConnectCodeModal.close();
      }

      this.openStatusModal({ name: integration.name, status, type: platform, firstSync });
    };

    return this.subscribeWebsockets(handler);
  }

  subscribeWebsockets = (handler) => this.$rootScope.$on('socket:integration:status', handler);

  openStatusModal = ({ name, status, autoclose, type, firstSync }) => {
    this.closeStatusModal();

    const title = INTEGRATION_MODAL[status][0] || status;
    const description = INTEGRATION_MODAL[status][1](name, firstSync);

    let syncInfo;
    let displayDiscoverSling;
    let timeout;

    if (firstSync && status === IntegrationStatus.enabled) {
      syncInfo = INTEGRATIONS_INFO[type]?.imported;
      displayDiscoverSling =
        !this.Authentication.user.settings.clientFlags?.adminOnboardingWelcomeShown;
    }

    this.modal = this.$uibModal.open({
      size: 's',
      templateUrl: statusHtml,
      controllerAs: 'ctrl',
      controller() {
        this.status = status;
        this.name = name;
        this.title = title;
        this.description = description;
        this.syncInfo = syncInfo;
        this.displayDiscoverSling = displayDiscoverSling;
      },
      postponeOpenedEducation: true,
    });
    this.modal.closed.then(() => timeout && clearTimeout(timeout));

    if (autoclose) {
      timeout = setTimeout(this.closeStatusModal, autoclose);
    }
  };

  closeStatusModal = () => this.modal && this.modal.close();

  showToastIntegrationCompleteModal() {
    this.closeToastIntegrationCompleteModal();
    const ctrl = this;

    this.toastIntegrationCompleteModal = this.$uibModal.open({
      size: '620',
      controllerAs: 'ctrl',
      controller() {
        // This callback is used by `modal-toast-connected`
        this.closeModalCallback = ctrl.closeToastIntegrationCompleteModal;
        this.currentUser = ctrl.Authentication.user;

        this.trackAnalyticsEvent = (eventName, eventValue) =>
          ctrl.SlingAnalytics.track(eventName, eventValue);

        this.importShiftSchedulesCallback = (dateFrom) => {
          ctrl.closeToastIntegrationCompleteModal();

          // Redirect to shifts
          ctrl.$state.go('shifts', { mode: 'week', tab: 'alllocations', date: dateFrom });

          // Open import in-progress modal
          ctrl.openStatusModal({
            name: IntegrationPlatform.toast,
            status: IntegrationStatus.shiftImport,
            type: PosIntegrations.toast,
            firstSync: false,
          });

          // Upon shift import completion, show success modal briefly
          // then display publish shifts education tooltip
          ctrl.$rootScope.$on('socket:shifts:import:complete', () => {
            ctrl.openStatusModal({
              name: IntegrationPlatform.toast,
              status: IntegrationStatus.enabled,
              type: PosIntegrations.toast,
              firstSync: false,
            });

            setTimeout(() => {
              ctrl.closeStatusModal();
            }, 3000);
          });
        };
      },
      templateUrl: toastModalUrl,
      postponeOpenedEducation: false,
      backdrop: 'static',
    });

    return this.toastIntegrationCompleteModal.result.ignoreCancel();
  }

  closeToastIntegrationCompleteModal = () =>
    this.toastIntegrationCompleteModal && this.toastIntegrationCompleteModal.close();

  async checkIntegrationMappingError(integration) {
    if (integration.status in this.mappingHandlers) {
      await this.mappingHandlers[integration.status](integration);
      return true;
    }

    return false;
  }

  async checkIntegrationMappingErrors(integrations) {
    for (const integration of integrations) {
      await this.checkIntegrationMappingError(integration);
    }
  }

  onChange(cb) {
    this.onIntegrationsChangeCb = cb;
    return () => {
      this.onIntegrationsChangeCb = null;
    };
  }

  showLocationMappingModal = async (integration) => {
    if (this._mappingModalShown) {
      return;
    }

    this._mappingModalShown = true;
    try {
      await this.$uibModal
        .open({
          name: 'LocationMappingCtrl',
          templateUrl: locationMappingModalUrl,
          controller: LocationMappingCtrl,
          controllerAs: 'ctrl',
          size: '620',
          backdrop: 'static',
          postponeOpenedEducation: true,
          resolve: {
            integration: () => integration,
            personaCollection(PersonaCollection) {
              'ngInject';

              return PersonaCollection.loadData();
            },
          },
        })
        .result.ignoreCancel();
    } finally {
      this._mappingModalShown = false;
    }
  };

  showUserMappingModal = async (integration) => {
    if (this._mappingModalShown) {
      return;
    }

    this._mappingModalShown = true;
    try {
      await this.$uibModal
        .open({
          name: 'UserMappingCtrl',
          templateUrl: userMappingModalUrl,
          controller: UserMappingCtrl,
          controllerAs: 'ctrl',
          size: '620',
          backdrop: 'static',
          resolve: {
            integration: () => integration,
            personaCollection(PersonaCollection) {
              'ngInject';

              return PersonaCollection.loadData();
            },
          },
        })
        .result.ignoreCancel();
    } finally {
      this._mappingModalShown = false;
    }
  };

  async getLocationsToMap(integration) {
    const response = await this.Authentication.sendRequest({
      method: 'get',
      path: `integrations/${integration.type}/unpersisted_locations`,
    });
    return response.data;
  }

  async getUsersToMap(integration) {
    const response = await this.Authentication.sendRequest({
      method: 'get',
      path: `integrations/${integration.type}/unpersisted_users`,
    });
    return response.data;
  }

  get enabledIntegrations() {
    return (this._integrations || []).reduce((dict, integration) => {
      if (integration.enabled) {
        dict[integration.type] = true;
      }

      return dict;
    }, {});
  }
}
